import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { actions as CollectionActions } from '@fenderdigital/business/collections';
import { getPathList, setCurrentPath } from '../actions/paths';
import { setMyPath } from '../actions/my-path';
import { getCourses, setCurrentCourse } from '../actions/courses';
import { getLessons } from '../actions/lessons';
import { getPractice } from '../actions/practice';
import getTabData from '../lib/tab-data';
import { getRelatedLessons } from '../actions/related-items';
import { getRecentlyViewed, getLessonsProgress } from '../actions/my-progress';
import Library from '../lib/library';

const { getCollectionWithID } = CollectionActions;

export default function useDataLoader() {
  const [isFetching, setIsfetching] = useState(true);
  const [conditionalProps, setConditionalProps] = useState({});
  const {
    pathSlug,
    level,
    instrument,
    collectionSlug,
    courseSlug,
    lessonSlug,
    practiceSlug,
  } = useParams();
  const { paths, myPath, collections, courses, lessons, practice, myProgress } = useSelector(
    state => state,
  );
  const dispatch = useDispatch();
  useEffect(() => {
    (async () => {
      let { results: pathList } = paths;
      if (pathSlug && !(pathSlug in pathList)) {
        const { items } = await dispatch(getPathList());
        const configuredPathListItems = {};
        items.forEach(item => (configuredPathListItems[item.slug] = item));
        pathList = configuredPathListItems;
      }

      const promiseList = [
        ...confirmOrFetchPath(pathList), // eslint-disable-line
        ...confirmOrFetchCollection(), // eslint-disable-line
        ...confirmOrFetchCourse(), // eslint-disable-line
        ...confirmOrFetchlesson(), // eslint-disable-line
        ...confirmOrFetchPractice(), // eslint-disable-line
      ];

      const resultslist = await Promise.all(promiseList);
      const { items } = resultslist.find(result => result) || { items: false };
      if (items) {
        if (lessonSlug) await getLessonProgress(items); // eslint-disable-line
        if (practiceSlug) await fetchTablature(items); // eslint-disable-line
      }
      setIsfetching(false);
    })();
  }, [pathSlug, level, courseSlug, lessonSlug, practiceSlug]);

  const confirmOrFetchPath = pathList => {
    const pathPromiseList = [];
    if (!pathSlug) return pathPromiseList;
    const { id: pathID } = pathList[pathSlug];
    const { id, playlists } = myPath;

    if (id !== pathID || !playlists) pathPromiseList.push(dispatch(setMyPath(pathID)));
    return pathPromiseList;
  };

  const confirmOrFetchCollection = () => {
    const collectionPromiseList = [];
    if (!collectionSlug) return collectionPromiseList;
    const { collection } = collections;
    const { slug: currentCollectionSlug } = collection || {};

    if (collectionSlug !== currentCollectionSlug)
      collectionPromiseList.push(
        dispatch(
          getCollectionWithID(collectionSlug, () =>
            Library.getCollectionBySlug(collectionSlug, instrument),
          ),
        ),
      );
    return collectionPromiseList;
  };

  const confirmOrFetchCourse = () => {
    const coursePromiseList = [];
    if (!courseSlug) return coursePromiseList;
    const { slugs: courseSlugs = {}, watching } = courses;
    const { currentCourse, currentLesson } = watching;
    const { slug: currentCourseSlug } = currentCourse || {};
    const { slug: currentLessonSlug } = currentLesson || {};
    const lessonIsCurrent = lessonSlug ? lessonSlug === currentLessonSlug : true;
    const practiceIsCurrent = practiceSlug ? practiceSlug === currentLessonSlug : true;

    if (!lessonSlug) coursePromiseList.push(dispatch(getRecentlyViewed(1)));

    if (!(courseSlug in courseSlugs))
      coursePromiseList.push(dispatch(getCourses({ slug: courseSlug })));
    if (courseSlug !== currentCourseSlug || !lessonIsCurrent || !practiceIsCurrent)
      coursePromiseList.push(dispatch(setCurrentCourse(courseSlug, practiceSlug || lessonSlug)));
    return coursePromiseList;
  };

  const confirmOrFetchlesson = () => {
    const lessonPromiseList = [];
    if (!lessonSlug) return lessonPromiseList;
    const { slugs: lessonSlugs = {} } = lessons;

    if (pathSlug && level)
      lessonPromiseList.push(dispatch(setCurrentPath(pathSlug, level, courseSlug, lessonSlug)));
    if (!(lessonSlug in lessonSlugs))
      lessonPromiseList.push(dispatch(getLessons({ slug: lessonSlug })));
    return lessonPromiseList;
  };

  const confirmOrFetchPractice = () => {
    const practicePromiseList = [];
    if (!practiceSlug) return practicePromiseList;
    const { slugs: practiceSlugs = {} } = practice;

    if (pathSlug && level)
      practicePromiseList.push(dispatch(setCurrentPath(pathSlug, level, courseSlug, practiceSlug)));
    if (!(practiceSlug in practiceSlugs)) {
      practicePromiseList.push(dispatch(getPractice({ slug: practiceSlug })));
    } else {
      practicePromiseList.push({ items: Object.values(practiceSlugs) });
    }
    return practicePromiseList;
  };

  const getLessonProgress = items => {
    const progressPromiseList = [];
    const lesson = items.find(({ slug }) => slug === lessonSlug);
    if (!lesson) return progressPromiseList;
    const { id, 'related-lessons': relatedLessons } = lesson || {};
    const { lessons: lessonsProgress } = myProgress;

    if (relatedLessons && relatedLessons.length)
      progressPromiseList.push(dispatch(getRelatedLessons(relatedLessons)));
    if (!(id in lessonsProgress)) progressPromiseList.push(dispatch(getLessonsProgress([id])));
    return Promise.all(progressPromiseList);
  };

  const fetchTablature = items => {
    const currentPractice = items.find(({ slug }) => slug === practiceSlug);

    if (currentPractice?.tabs) {
      return getTabData(currentPractice?.tabs[0]['tab-file']).then(tabXML =>
        import('../routes/practice/metronome.worker').then(({ default: Worker }) =>
          setConditionalProps({ tabXML, worker: new Worker() }),
        ),
      );
    }
  };

  return { isFetching, conditionalProps };
}
